import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { cart as cartApi, catalog as catalogApi, auth as authApi, orders as ordersApi } from '@/services/api';

export const useCatalogStore = defineStore('catalog', () => {
  const home = ref(null);
  const products = ref([]);
  const categories = ref([]);
  const loading = ref(false);
  const error = ref(null);

  async function loadHome() {
    loading.value = true;
    error.value = null;
    try {
      home.value = await catalogApi.home();
      categories.value = home.value.categories;
    } catch (e) {
      error.value = e.message || 'Failed to load home feed';
    } finally {
      loading.value = false;
    }
  }

  async function loadProducts(params = {}) {
    loading.value = true;
    error.value = null;
    try {
      products.value = await catalogApi.products(params);
    } catch (e) {
      error.value = e.message;
    } finally {
      loading.value = false;
    }
  }

  return { home, products, categories, loading, error, loadHome, loadProducts };
});

export const useCartStore = defineStore('cart', () => {
  const summary = ref({ items: [], subtotal: 0, delivery_fee: 0, vat: 0, total: 0, item_count: 0 });
  const loading = ref(false);
  const error = ref(null);
  const itemCount = computed(() => summary.value.item_count || 0);

  async function refresh() {
    loading.value = true;
    error.value = null;
    try {
      summary.value = await cartApi.show();
    } catch (e) {
      error.value = e.message;
    } finally {
      loading.value = false;
    }
  }

  async function add(productId, qty = 1) {
    error.value = null;
    try {
      summary.value = await cartApi.add(productId, qty);
    } catch (e) {
      error.value = e.message;
      throw e;
    }
  }

  async function setQty(productId, qty) {
    error.value = null;
    try {
      summary.value = await cartApi.setQty(productId, qty);
    } catch (e) {
      error.value = e.message;
      throw e;
    }
  }

  function reset() {
    summary.value = { items: [], subtotal: 0, delivery_fee: 0, vat: 0, total: 0, item_count: 0 };
  }

  return { summary, loading, error, itemCount, refresh, add, setQty, reset };
});

export const useToastStore = defineStore('toasts', () => {
  const list = ref([]);
  function push(text, kind = 'info', ttl = 2400) {
    const id = Date.now() + Math.random();
    list.value.push({ id, text, kind });
    setTimeout(() => {
      list.value = list.value.filter((t) => t.id !== id);
    }, ttl);
  }
  return { list, push };
});

export const useAuthStore = defineStore('auth', () => {
  const user = ref(null);
  const loading = ref(false);
  const error = ref(null);
  const isLoggedIn = computed(() => user.value !== null);

  function _persistToken(token) {
    if (token) window.localStorage.setItem('meed.token', token);
    else window.localStorage.removeItem('meed.token');
  }

  async function init() {
    const token = window.localStorage.getItem('meed.token');
    if (!token) return;
    try {
      user.value = await authApi.me();
    } catch {
      _persistToken(null);
      user.value = null;
    }
  }

  async function login(email, password) {
    loading.value = true;
    error.value = null;
    try {
      const data = await authApi.login(email, password);
      user.value = data.user;
      _persistToken(data.token);
    } catch (e) {
      error.value = e.errors?.email?.[0] || e.message || 'Login failed';
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function register(payload) {
    loading.value = true;
    error.value = null;
    try {
      const data = await authApi.register(payload);
      user.value = data.user;
      _persistToken(data.token);
    } catch (e) {
      error.value = e.errors ? Object.values(e.errors).flat()[0] : e.message || 'Registration failed';
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function logout() {
    try { await authApi.logout(); } catch { /* best-effort */ }
    user.value = null;
    _persistToken(null);
  }

  return { user, loading, error, isLoggedIn, init, login, register, logout };
});

export const useOrderStore = defineStore('customerOrders', () => {
  const list = ref([]);
  const loading = ref(false);
  const error = ref(null);

  async function load(params = {}) {
    loading.value = true;
    error.value = null;
    try {
      list.value = await ordersApi.list(params);
    } catch (e) {
      error.value = e.message;
      list.value = [];
    } finally {
      loading.value = false;
    }
  }

  return { list, loading, error, load };
});
